/**
 * WASM module loader.
 *
 * Handles async initialization of the WASM module.
 */

import type { WasmModule } from './types.js';
// @ts-ignore -- generated at build time by `npm run build:wasm` (wasm-pack --target web).
import * as wasm from '../../../out/pkg/diff_wasm.js';

let wasmModule: WasmModule | null = null;
let initPromise: Promise<WasmModule> | null = null;

/**
 * Initialize the WASM module.
 *
 * This must be called before using any WASM-based diff functions.
 * It's safe to call multiple times — subsequent calls are no-ops.
 *
 * @returns Promise that resolves when WASM is ready
 */
export async function initWasm(): Promise<WasmModule> {
	if (wasmModule) {
		return wasmModule;
	}
	if (initPromise) {
		return initPromise;
	}

	initPromise = (async () => {
		const wasmUrl = new URL('../../../out/pkg/diff_wasm_bg.wasm', import.meta.url);
		// Node's undici fetch does not support file:// URLs, so read bytes ourselves.
		let source: BufferSource | URL = wasmUrl;
		if (typeof process !== 'undefined' && process.versions?.node) {
			const { readFile } = await import('node:fs/promises');
			source = await readFile(wasmUrl);
		}
		await wasm.default({ module_or_path: source });
		wasm.init();
		wasmModule = wasm as unknown as WasmModule;
		return wasmModule;
	})();

	return initPromise;
}

/**
 * Get the WASM module synchronously.
 * 
 * @throws Error if WASM hasn't been initialized yet
 * @returns The WASM module
 */
export function getWasm(): WasmModule {
	if (!wasmModule) {
		throw new Error('WASM not initialized. Call initWasm() first and await it.');
	}
	return wasmModule;
}

/**
 * Check if the WASM module is initialized.
 */
export function isWasmInitialized(): boolean {
	return wasmModule !== null;
}

/**
 * Reset the WASM module (useful for testing).
 * @internal
 */
export function resetWasm(): void {
	wasmModule = null;
	initPromise = null;
}
